
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, AlertTriangle } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';

interface Permission {
  action: string;
  ceo: boolean;
  cfo: boolean;
  controller: boolean | 'optional';
  treasurer: boolean;
  analyst: boolean;
}

const permissions: Permission[] = [
  {
    action: 'View Payments',
    ceo: true,
    cfo: true,
    controller: true,
    treasurer: true,
    analyst: true
  },
  {
    action: 'Make Payments',
    ceo: false,
    cfo: true,
    controller: true,
    treasurer: true,
    analyst: false
  },
  {
    action: 'FX Transfers',
    ceo: false,
    cfo: true,
    controller: 'optional',
    treasurer: true,
    analyst: false
  },
  {
    action: 'Edit Agent Logic',
    ceo: false,
    cfo: true,
    controller: false,
    treasurer: true,
    analyst: false
  },
  {
    action: 'Override AI Agents',
    ceo: false,
    cfo: true,
    controller: false,
    treasurer: true,
    analyst: false
  },
  {
    action: 'Simulation Mode',
    ceo: true,
    cfo: true,
    controller: true,
    treasurer: true,
    analyst: false
  }
];

export function PermissionsMatrix() {
  const { settings } = useSettings();

  const getPermissionIcon = (permission: boolean | 'optional', isCurrentUser: boolean) => {
    if (permission === true) {
      return <Check className={`w-4 h-4 ${isCurrentUser ? 'text-success' : 'text-success'}`} />;
    } else if (permission === 'optional') {
      return <AlertTriangle className={`w-4 h-4 ${isCurrentUser ? 'text-warning' : 'text-warning'}`} />;
    } else {
      return <X className={`w-4 h-4 ${isCurrentUser ? 'text-destructive' : 'text-destructive'}`} />;
    }
  };

  const getRoleBadge = (role: string, isCurrentUser: boolean) => {
    return (
      <Badge 
        variant={isCurrentUser ? "default" : "outline"}
        className={isCurrentUser ? "bg-accent text-primary" : ""}
      >
        {role.toUpperCase()}
        {isCurrentUser && " (You)"}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Permissions Matrix</CardTitle>
        <p className="text-sm text-muted-foreground">
          Role-based access control for payments and AI agent features
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-medium">Action</th>
                <th className="text-center p-3 font-medium">
                  {getRoleBadge('CEO', settings.userRole === 'ceo')}
                </th>
                <th className="text-center p-3 font-medium">
                  {getRoleBadge('CFO', settings.userRole === 'cfo')}
                </th>
                <th className="text-center p-3 font-medium">
                  {getRoleBadge('Controller', settings.userRole === 'controller')}
                </th>
                <th className="text-center p-3 font-medium">
                  {getRoleBadge('Treasurer', settings.userRole === 'treasurer')}
                </th>
                <th className="text-center p-3 font-medium">
                  {getRoleBadge('Analyst', settings.userRole === 'analyst')}
                </th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((permission, index) => (
                <tr key={index} className="border-b hover:bg-muted/50">
                  <td className="p-3 font-medium">{permission.action}</td>
                  <td className="text-center p-3">
                    {getPermissionIcon(permission.ceo, settings.userRole === 'ceo')}
                  </td>
                  <td className="text-center p-3">
                    {getPermissionIcon(permission.cfo, settings.userRole === 'cfo')}
                  </td>
                  <td className="text-center p-3">
                    {getPermissionIcon(permission.controller, settings.userRole === 'controller')}
                  </td>
                  <td className="text-center p-3">
                    {getPermissionIcon(permission.treasurer, settings.userRole === 'treasurer')}
                  </td>
                  <td className="text-center p-3">
                    {getPermissionIcon(permission.analyst, settings.userRole === 'analyst')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Check className="w-4 h-4 text-success" />
            <span>Allowed</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <span>Optional/Requires Auth</span>
          </div>
          <div className="flex items-center gap-1">
            <X className="w-4 h-4 text-destructive" />
            <span>Not Allowed</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
